import { PICTOGRAM, PictogramKey } from "encamp-shared/src/waste/dotPictograms";
import {
  HazardLabelDesign,
  HAZARD_LABEL_CORNER_RADIUS_RATIO,
  HAZARD_LABEL_NUMBER_TOP_RATIO,
  HAZARD_LABEL_UNDERLINE_GAP_RATIO,
  HAZARD_LABEL_UNDERLINE_THICKNESS_RATIO,
  HAZARD_LABEL_UNDERLINE_WIDTH_RATIO,
  HAZARD_LABEL_SYMBOL_CENTER_RATIO,
  HAZARD_LABEL_SYMBOL_SIZE_RATIO,
  HAZARD_LABEL_SYMBOL_WIDTH_RATIO,
  HAZARD_LABEL_UPPER_HALF_CENTER_RATIO,
  HAZARD_LABEL_UPPER_HALF_WIDTH_RATIO,
  HAZARD_LABEL_WHITE_OUTLINE_RATIO,
  HazardLabelBackground,
} from "encamp-shared/src/waste/hazardClassDiamond";
import {
  backgroundHasWhite,
  diamondPath,
  stripePaths,
} from "encamp-shared/src/waste/hazardLabelGeometry";
import { RULE_COLOR } from "encamp-shared/src/waste/hazardousWasteLabelVisuals";
import { ReactElement } from "react";

/**
 * Oak's DOM-SVG mirror of the printed label's `HazardLabelDiamond`
 * (apps/lambdas/pdf-generator/src/generators/hazardousWasteLabelDiamond.tsx).
 * Oak cannot import that file -- it renders with `@react-pdf/renderer`
 * primitives, which do not exist outside pdf-generator. Only the element tree
 * differs: the shapes and colours both draw come from
 * encamp-shared/waste/hazardLabelGeometry, so the two cannot drift.
 */

/** The design's background, drawn UNROTATED and clipped to the diamond. */
function HazardLabelBackgroundShape({
  background,
  size,
}: {
  background: HazardLabelBackground;
  size: number;
}): ReactElement {
  if (background.kind === "SOLID") {
    return <rect x={0} y={0} width={size} height={size} fill={background.color} />;
  }
  if (background.kind === "SPLIT") {
    // The split runs along the diamond's horizontal diagonal -- the line
    // through its left and right points, which is the box's horizontal
    // midline now that nothing is rotated.
    return (
      <>
        <rect x={0} y={0} width={size} height={size / 2} fill={background.topColor} />
        <rect x={0} y={size / 2} width={size} height={size / 2} fill={background.bottomColor} />
      </>
    );
  }
  return (
    <>
      <rect x={0} y={0} width={size} height={size} fill={background.groundColor} />
      {stripePaths(size).map((d, index) => (
        <path key={index} d={d} fill={background.stripeColor} data-testid="design-stripe" />
      ))}
    </>
  );
}

/**
 * The symbol in the diamond's upper half. Not rotated with the background --
 * a rotated symbol would be lying on its side. Mirrors
 * hazardousWasteLabelDiamond.tsx:120-160.
 */
function HazardLabelSymbol({
  design,
  size,
}: {
  design: HazardLabelDesign;
  size: number;
}): ReactElement | null {
  if (!design.pictogram) return null;
  const pictogram = PICTOGRAM[design.pictogram];
  const color = design.pictogramColor ?? design.textColor;
  // The box is wider than it is tall, and the symbol is fitted inside it
  // preserving its own aspect. A square box sized by height alone shrinks the
  // wide symbols badly -- the corrosion symbol is over 2:1 and the gas
  // cylinder over 3:1.
  const aspect = pictogram.viewBox.width / pictogram.viewBox.height;
  // Class 9's bars ARE its upper half, edge to edge, so they are laid out by
  // width rather than fitted into the centred symbol slot.
  const spansUpperHalf = design.pictogramSpansUpperHalf === true;
  const boxHeight = size * HAZARD_LABEL_SYMBOL_SIZE_RATIO;
  const boxWidth = boxHeight * HAZARD_LABEL_SYMBOL_WIDTH_RATIO;
  const drawnWidth = spansUpperHalf
    ? size * HAZARD_LABEL_UPPER_HALF_WIDTH_RATIO
    : Math.min(boxWidth, boxHeight * aspect);
  const drawnHeight = drawnWidth / aspect;
  const centerRatio = spansUpperHalf
    ? HAZARD_LABEL_UPPER_HALF_CENTER_RATIO
    : HAZARD_LABEL_SYMBOL_CENTER_RATIO;
  return (
    <svg
      width={drawnWidth}
      height={drawnHeight}
      // Every pictogram is cropped to its own bounding box inside the source
      // design's coordinate space (dotPictograms.ts), so the viewBox must
      // carry the symbol's minX/minY -- "0 0 W H" puts the symbol outside the
      // viewport and clips it to a sliver. This bug was introduced twice on
      // the PDF side before being caught by a rendered-image check.
      viewBox={`${pictogram.viewBox.minX} ${pictogram.viewBox.minY} ${pictogram.viewBox.width} ${pictogram.viewBox.height}`}
      style={{
        position: "absolute",
        top: size * centerRatio - drawnHeight / 2,
        left: size / 2 - drawnWidth / 2,
      }}
    >
      {pictogram.paths.map((path, index) => (
        <path
          key={index}
          d={path.d}
          fill={path.fill ?? color}
          fillRule={path.fillRule}
          stroke={path.stroke}
          strokeWidth={path.strokeWidth}
        />
      ))}
    </svg>
  );
}

/**
 * One DOT design: the diamond, its background, its symbol, and its class
 * number. Mirrors `HazardLabelDiamond` in hazardousWasteLabelDiamond.tsx.
 * 49 CFR 172.402(b) requires the number on primary and subsidiary labels
 * alike. The number is drawn only when `hazardClass` is given.
 */
export function PreviewHazardLabel({
  design,
  hazardClass,
  size,
  numberSize,
}: {
  design: HazardLabelDesign;
  hazardClass?: string;
  size: number;
  numberSize: number;
}): ReactElement {
  const cornerRadius = size * HAZARD_LABEL_CORNER_RADIUS_RATIO;
  const outline = diamondPath(size, cornerRadius);
  // A white area on a white label has no visible edge, so any design
  // carrying one gets a thin outline -- not just the solid-white ones: class
  // 8 is white over black and class 7 is yellow over white, and without this
  // their white half simply disappears into the page, leaving a triangle
  // where a diamond should be.
  const hasWhiteArea = backgroundHasWhite(design.background);
  const clipId = `design-${size}-${design.background.kind}-${hazardClass ?? "sub"}`;
  return (
    <div
      data-testid="label-preview-hazard-diamond"
      data-has-outline={hasWhiteArea}
      style={{ position: "relative", width: size, height: size }}
    >
      <svg width={size} height={size}>
        <defs>
          <clipPath id={clipId}>
            <path d={outline} />
          </clipPath>
        </defs>
        <g clipPath={`url(#${clipId})`}>
          <HazardLabelBackgroundShape background={design.background} size={size} />
        </g>
        {hasWhiteArea ? (
          <path
            d={outline}
            fill="none"
            stroke={RULE_COLOR}
            strokeWidth={size * HAZARD_LABEL_WHITE_OUTLINE_RATIO}
          />
        ) : null}
      </svg>
      <HazardLabelSymbol design={design} size={size} />
      {hazardClass ? (
        <div
          style={{
            position: "absolute",
            top: size * HAZARD_LABEL_NUMBER_TOP_RATIO,
            left: 0,
            width: size,
            textAlign: "center",
          }}
        >
          <span
            style={{
              color: design.textColor,
              fontWeight: 700,
              fontSize: numberSize,
            }}
          >
            {hazardClass}
          </span>
          {design.underlineNumber === true ? (
            <div
              data-testid="hazard-label-number-underline"
              style={{
                margin: `${numberSize * HAZARD_LABEL_UNDERLINE_GAP_RATIO}px auto 0`,
                width: numberSize * HAZARD_LABEL_UNDERLINE_WIDTH_RATIO,
                height: numberSize * HAZARD_LABEL_UNDERLINE_THICKNESS_RATIO,
                backgroundColor: design.textColor,
              }}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * One RCRA characteristic symbol, sized by HEIGHT with width following the
 * symbol's own aspect. Mirrors `RcraPictogram` in
 * hazardousWasteLabelComponents.tsx:293-313.
 */
export function RcraPictogram({
  pictogram,
  height,
}: {
  pictogram: PictogramKey;
  height: number;
}): ReactElement {
  const { viewBox, paths } = PICTOGRAM[pictogram];
  const width = height * (viewBox.width / viewBox.height);
  return (
    <svg
      width={width}
      height={height}
      viewBox={`${viewBox.minX} ${viewBox.minY} ${viewBox.width} ${viewBox.height}`}
    >
      {paths.map((path, index) => (
        <path
          key={index}
          d={path.d}
          fill={path.fill ?? "#000000"}
          fillRule={path.fillRule}
          stroke={path.stroke}
          strokeWidth={path.strokeWidth}
        />
      ))}
    </svg>
  );
}

/**
 * The 49 CFR 172.312 package orientation mark: two upward arrows, shown on a
 * combination packaging containing liquid. Mirrors `OrientationArrows` in
 * hazardousWasteLabelDiamond.tsx:246-259.
 */
export function OrientationArrows({ size }: { size: number }): ReactElement {
  const pictogram = PICTOGRAM.ORIENTATION_ARROWS;
  return (
    <svg
      data-testid="label-preview-orientation-arrows"
      width={size}
      height={size}
      viewBox={`${pictogram.viewBox.minX} ${pictogram.viewBox.minY} ${pictogram.viewBox.width} ${pictogram.viewBox.height}`}
    >
      {pictogram.paths.map((path, index) => (
        <path key={index} d={path.d} fill={path.fill ?? "#000000"} />
      ))}
    </svg>
  );
}
